// components/session-data/ScopedUsersListener.jsx
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import firebase from "firebase/app";
import { db } from "../../firebase/firebase.utils";
import { setUsers } from "../../redux/user/user.actions";
import { useCollabProjects } from "../collab-projects/CollabProjectsProvider";

const validId = (id) => typeof id === "string" && id.length > 0 && !id.includes("/");

export default function ScopedUsersListener() {
  const uid = useSelector((state) => state.user.currentUser?.uid);
  const { spaces, allProjects } = useCollabProjects();
  const dispatch = useDispatch();
  const saved = useRef({ uid: null, people: new Map() });
  // Titles, colors and task changes must not restart user queries.
  const idsKey = JSON.stringify([...new Set([
    uid,
    ...spaces.flatMap((space) => space.members || []),
    ...allProjects.flatMap((project) => project.members || []),
  ].filter(validId))].sort());

  useEffect(() => {
    let active = true;
    const ids = uid ? JSON.parse(idsKey) : [];
    const previous = saved.current.uid === uid ? saved.current.people : new Map();
    const people = new Map(ids.filter((id) => previous.has(id)).map((id) => [id, previous.get(id)]));
    saved.current = { uid, people };
    const publish = () => dispatch(setUsers(ids.flatMap((id) => people.has(id) ? [people.get(id)] : [])));
    publish();
    const stops = [];
    // Ten IDs per query also works with the older Firebase v8 SDK.
    for (let i = 0; i < ids.length; i += 10) {
      const chunk = ids.slice(i, i + 10);
      stops.push(db.collection("users")
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .onSnapshot(
          (snap) => {
            if (!active) return;
            chunk.forEach((id) => people.delete(id));
            snap.docs.forEach((doc) => people.set(doc.id, { ...doc.data(), uid: doc.id }));
            publish();
          },
          (error) => {
            if (!active) return;
            chunk.forEach((id) => people.delete(id));
            publish();
            console.error("Member profiles could not be loaded:", error);
          },
        ));
    }
    return () => { active = false; stops.forEach((stop) => stop()); };
  }, [uid, idsKey, dispatch]);

  useEffect(() => () => dispatch(setUsers([])), [dispatch]);
  return null;
}
